import { customAlphabet } from "nanoid";
import { z } from "zod";

const alphabet = "0123456789abcdefghijklmnopqrstuvwxyz";
export const idBodyLength = 16;
export const shortIdLength = 10;

const randomBody = customAlphabet(alphabet, idBodyLength);
const randomShort = customAlphabet(alphabet, shortIdLength);

export const idPrefixes = {
  account: "acc",
  masterCategory: "mcg",
  category: "cat",
  budgetEntry: "bud",
  payee: "pay",
  payeeRule: "prl",
  transaction: "txn",
  scheduled: "sch",
  memory: "mem",
  user: "usr",
  session: "ses",
  apiKey: "key",
  importBatch: "imb",
  importRow: "imr",
  changeset: "chg",
} as const;

export type IdKind = keyof typeof idPrefixes;

function idSchema<K extends IdKind>(kind: K) {
  return z
    .string()
    .regex(new RegExp(`^${idPrefixes[kind]}_[0-9a-z]{${idBodyLength}}$`), `Invalid ${kind} id`)
    .brand(kind);
}

export const accountIdSchema = idSchema("account");
export const masterCategoryIdSchema = idSchema("masterCategory");
export const categoryIdSchema = idSchema("category");
export const budgetEntryIdSchema = idSchema("budgetEntry");
export const payeeIdSchema = idSchema("payee");
export const payeeRuleIdSchema = idSchema("payeeRule");
export const transactionIdSchema = idSchema("transaction");
export const scheduledIdSchema = idSchema("scheduled");
export const memoryIdSchema = idSchema("memory");
export const userIdSchema = idSchema("user");
export const sessionIdSchema = idSchema("session");
export const apiKeyIdSchema = idSchema("apiKey");
export const importBatchIdSchema = idSchema("importBatch");
export const importRowIdSchema = idSchema("importRow");
export const changesetIdSchema = idSchema("changeset");

export type AccountId = z.infer<typeof accountIdSchema>;
export type MasterCategoryId = z.infer<typeof masterCategoryIdSchema>;
export type CategoryId = z.infer<typeof categoryIdSchema>;
export type BudgetEntryId = z.infer<typeof budgetEntryIdSchema>;
export type PayeeId = z.infer<typeof payeeIdSchema>;
export type PayeeRuleId = z.infer<typeof payeeRuleIdSchema>;
export type TransactionId = z.infer<typeof transactionIdSchema>;
export type ScheduledId = z.infer<typeof scheduledIdSchema>;
export type MemoryId = z.infer<typeof memoryIdSchema>;
export type UserId = z.infer<typeof userIdSchema>;
export type SessionId = z.infer<typeof sessionIdSchema>;
export type ApiKeyId = z.infer<typeof apiKeyIdSchema>;
export type ImportBatchId = z.infer<typeof importBatchIdSchema>;
export type ImportRowId = z.infer<typeof importRowIdSchema>;
export type ChangesetId = z.infer<typeof changesetIdSchema>;

interface IdTypes {
  account: AccountId;
  masterCategory: MasterCategoryId;
  category: CategoryId;
  budgetEntry: BudgetEntryId;
  payee: PayeeId;
  payeeRule: PayeeRuleId;
  transaction: TransactionId;
  scheduled: ScheduledId;
  memory: MemoryId;
  user: UserId;
  session: SessionId;
  apiKey: ApiKeyId;
  importBatch: ImportBatchId;
  importRow: ImportRowId;
  changeset: ChangesetId;
}

export function newId<K extends IdKind>(kind: K): IdTypes[K] {
  return `${idPrefixes[kind]}_${randomBody()}` as IdTypes[K];
}

/** For transient things (requests, job runs) that are read in logs but never stored as rows. */
export function newShortId(): string {
  return randomShort();
}

/**
 * A deterministic 16-character body from arbitrary key parts. Two devices that derive
 * the same entity (a budget cell, a scheduled occurrence) get the same ID and merge.
 */
export function hashBody(...parts: string[]): string {
  const input = parts.join("\u001f");
  let out = "";
  for (let seed = 0; out.length < idBodyLength; seed++) {
    out += cyrb53(input, seed).toString(36).padStart(11, "0");
  }
  return out.slice(0, idBodyLength);
}

export function deriveId<K extends IdKind>(kind: K, ...parts: string[]): IdTypes[K] {
  return `${idPrefixes[kind]}_${hashBody(...parts)}` as IdTypes[K];
}

function cyrb53(str: string, seed: number): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}
